import { useState, useEffect, useCallback, useRef } from "react";
import { Auth } from "aws-amplify";
import { useSetRecoilState, useRecoilValue } from "recoil";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldAlert } from "lucide-react";
import { onSession401 } from "@/services/sessionEvents";
import { accessToken, idToken, refreshToken } from "@/store/auth";
import { pendingRequestQueue } from "@/services/axiosInterceptor";

const COUNTDOWN_SECONDS = 30;

// Shown when the server returns 401.
// Gives the user a chance to refresh their Cognito session and stay in the app.
// If refresh fails or the countdown expires, performs a clean logout.
const SessionExpiredContainer = ({ handleLogout }) => {
  const [visible, setVisible] = useState(false);
  const [remaining, setRemaining] = useState(COUNTDOWN_SECONDS);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);

  const storedRefreshToken = useRecoilValue(refreshToken);
  const setAccessToken = useSetRecoilState(accessToken);
  const setIdToken = useSetRecoilState(idToken);
  const setRefreshToken = useSetRecoilState(refreshToken);

  const countdownRef = useRef(null);
  const startTimeRef = useRef(null);
  // Guard: only show one dialog even if multiple 401s fire concurrently.
  const activeRef = useRef(false);

  const clearCountdown = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  const doLogout = useCallback(() => {
    clearCountdown();
    activeRef.current = false;
    setVisible(false);
    // Drain the queue with a rejection so callers get a clean error.
    pendingRequestQueue.flush(null);
    handleLogout?.();
  }, [handleLogout]);

  const startCountdown = useCallback(() => {
    startTimeRef.current = Date.now();
    setRemaining(COUNTDOWN_SECONDS);
    countdownRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const left = Math.max(0, Math.round(COUNTDOWN_SECONDS - elapsed));
      setRemaining(left);
      if (left <= 0) doLogout();
    }, 500);
  }, [doLogout]);

  // Listen for 401 events from the interceptor.
  useEffect(() => {
    return onSession401(() => {
      if (activeRef.current) return; // already showing
      activeRef.current = true;
      setRefreshError(false);
      setRefreshing(false);
      setVisible(true);
      startCountdown();
    });
  }, [startCountdown]);

  const handleContinue = useCallback(async () => {
    clearCountdown();
    setRefreshing(true);
    try {
      // Get the current Cognito user then force-refresh via the stored refresh token.
      // Auth.currentSession() only refreshes if Amplify thinks the token is locally expired;
      // calling refreshSession directly always makes a network call to Cognito.
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const { CognitoRefreshToken } = await import("amazon-cognito-identity-js");
      const cognitoRefreshToken = new CognitoRefreshToken({ RefreshToken: storedRefreshToken });

      const session = await new Promise((resolve, reject) => {
        cognitoUser.refreshSession(cognitoRefreshToken, (err, newSession) => {
          if (err) reject(err);
          else resolve(newSession);
        });
      });

      const newAccessToken = session.getAccessToken().getJwtToken();
      const newIdToken = session.getIdToken().getJwtToken();
      const newRefreshToken = session.getRefreshToken().getToken();

      setAccessToken(newAccessToken);
      setIdToken(newIdToken);
      setRefreshToken(newRefreshToken);

      pendingRequestQueue.flush(newAccessToken);

      activeRef.current = false;
      setRefreshing(false);
      setVisible(false);
    } catch (err) {
      console.error("[SessionExpiredContainer] handleContinue failed:", err);
      setRefreshing(false);
      setRefreshError(true);
      setTimeout(() => doLogout(), 3000);
    }
  }, [storedRefreshToken, setAccessToken, setIdToken, setRefreshToken, doLogout]);

  const handleImmediateLogout = useCallback(() => {
    doLogout();
  }, [doLogout]);

  if (!visible) return null;

  return (
    <Dialog open={visible} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-sm w-[calc(100%-2rem)] rounded-xl p-0 overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="items-center text-center gap-3 px-6 pt-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50 dark:bg-red-950">
            <ShieldAlert className="w-6 h-6 text-red-500 dark:text-red-400" />
          </div>
          <DialogTitle className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Session Expired
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
            {refreshError ? (
              <span className="text-red-500 dark:text-red-400">
                Your session could not be restored. Logging out…
              </span>
            ) : (
              <>
                Your session has expired. Would you like to continue where you
                left off, or log out?{" "}
                <span className="font-semibold text-red-500">{remaining}s</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {!refreshError && (
          <div className="mx-6 mt-1 mb-2 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            <div
              className="h-full rounded-full bg-red-400 transition-all duration-500 ease-linear"
              style={{ width: `${(remaining / COUNTDOWN_SECONDS) * 100}%` }}
            />
          </div>
        )}

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 px-6 pb-6 pt-2">
          <Button
            variant="outline"
            onClick={handleImmediateLogout}
            disabled={refreshing || refreshError}
            className="w-full sm:w-auto cursor-pointer border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Log Out
          </Button>
          <Button
            onClick={handleContinue}
            disabled={refreshing || refreshError}
            className="w-full sm:w-auto cursor-pointer font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            {refreshing ? "Restoring…" : "Continue Session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionExpiredContainer;
